import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from "@angular/router";

export class CustomReuseStrategy implements RouteReuseStrategy {
  private storedHandles = new Map<string, DetachedRouteHandle>();
  private expirationTimes = new Map<string, number>();
  private readonly EXPIRATION_DURATION = 2 * 60 * 1000; // 2 minutes in milliseconds

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const key = this.getRouteKey(route);
    this.storedHandles.set(key, handle);
    this.expirationTimes.set(key, Date.now() + this.EXPIRATION_DURATION);
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return !!route.data?.["shouldReuse"];
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getRouteKey(route);
    return this.storedHandles.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getRouteKey(route);
    const expirationTime = this.expirationTimes.get(key);

    if (expirationTime && Date.now() > expirationTime) {
      this.storedHandles.delete(key);
      this.expirationTimes.delete(key);
      return null;
    }

    return this.storedHandles.get(key) ?? null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  private getRouteKey(route: ActivatedRouteSnapshot): string {
    const segments = route.url.map((segment) => segment.path).join("/");
    const parent = route.parent;
    const parentSegments = parent?.url.map((segment) => segment.path).join("/") ?? "";
    const key = [parentSegments, segments].join("/");

    return key || "root";
  }
}
